import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '@/store/store-hooks'
import { selectMatterById } from '@/matter/store/selectors'
import type { MatterRead } from '@/matter/store/schemas'
import { matterPaths } from '@/routing/paths'
import ComingSoonCard from '@/components/coming-soon/ComingSoonCard'

type MatterSubject = Pick<MatterRead, 'viewer_role'> | null | undefined

export type MatterFeatureGuardProps = {
  /**
   * Permission predicate from `@/matter/utils/matter-permissions` (e.g. canViewTimeline).
   * Receives the matter from the URL; returns whether the current user
   * may use the feature. These already short-circuit to `true` when the COLLABORATION
   * flag is off, so the guard is a no-op in that case.
   */
  canAccess: (matter: MatterSubject) => boolean
}

/**
 * Route guard for owner-only matter features (Matter Record).
 *
 * Non-owners (accepted collaborators) who navigate directly to a gated route get a
 * "Coming soon" card instead of the feature. Owners are unaffected.
 *
 * Relies on MatterLayout (parent) having already loaded matters and validated matter
 * accessibility before this renders, so the matter is present here.
 */
export default function MatterFeatureGuard({ canAccess }: MatterFeatureGuardProps) {
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))

  if (matterId && !canAccess(matter)) {
    return <ComingSoonCard actionLabel="Back to Matter" onAction={() => navigate(matterPaths.overview(matterId))} />
  }

  return <Outlet />
}
